/**
 * Google Docs–style comment anchors: original quoted text stays in `targetText`;
 * live TipTap marks track position through edits; `anchor.excerpt` holds current passage text.
 * Reanchor order: marks → stored range → blockId-scoped quote → global quote → stale.
 */

import type { Editor } from "@tiptap/react";
import type { Node as PMNode } from "@tiptap/pm/model";
import { blockRangeById } from "@repo/shared/blockIdAtPos";
import {
  normalizeCommentAnchorText,
  pickCommentAnchorRange,
} from "@repo/shared/chapterCommentAnchor";
import type { Chapter, ChapterComment } from "../../shared/models";
import {
  findChapterCommentAnnotationRange,
  findTargetTextRangeInEditor,
} from "./commentEditorMarks";

function normalizeAnchorText(value: string): string {
  return normalizeCommentAnchorText(value);
}

function isValidTextRange(doc: PMNode, from: number, to: number): boolean {
  if (!Number.isFinite(from) || !Number.isFinite(to)) return false;
  const size = doc.content.size;
  if (from < 0 || to <= from || to > size) return false;
  try {
    doc.resolve(from);
    doc.resolve(to);
    return true;
  } catch {
    return false;
  }
}

function safeDocTextBetween(doc: PMNode, from: number, to: number): string {
  if (!isValidTextRange(doc, from, to)) return "";
  try {
    return doc.textBetween(from, to, " ", " ");
  } catch {
    return "";
  }
}

function docIncludesOriginal(editor: Editor, original: string): boolean {
  const o = normalizeAnchorText(original).toLowerCase();
  if (!o) return true;
  return normalizeAnchorText(editor.state.doc.textContent).toLowerCase().includes(o);
}

function findTargetTextInBlock(
  editor: Editor,
  blockId: string | null | undefined,
  targetText: string
): { from: number; to: number } | null {
  if (!blockId || !targetText.trim()) return null;
  const block = blockRangeById(editor.state.doc as Parameters<typeof blockRangeById>[0], blockId);
  if (!block) return null;
  const sliceText = safeDocTextBetween(editor.state.doc, block.from, block.to);
  if (!sliceText) return null;
  const lower = sliceText.toLowerCase();
  const needle = targetText.toLowerCase();
  let idx = lower.indexOf(needle);
  if (idx < 0) {
    const compactNeedle = normalizeAnchorText(targetText).toLowerCase();
    const compactHay = normalizeAnchorText(sliceText).toLowerCase();
    const cIdx = compactHay.indexOf(compactNeedle);
    if (cIdx < 0) return null;
    // Approximate: use first literal word match inside block
    const firstWord = compactNeedle.split(" ")[0] ?? "";
    idx = firstWord ? lower.indexOf(firstWord) : -1;
    if (idx < 0) return null;
    const end = Math.min(block.to, block.from + idx + targetText.length);
    const from = block.from + idx;
    if (from < end && isValidTextRange(editor.state.doc, from, end)) return { from, to: end };
    return null;
  }
  const from = block.from + idx;
  const to = from + targetText.length;
  if (isValidTextRange(editor.state.doc, from, to)) return { from, to };
  return null;
}

/** Resolve the best [from, to) for highlighting — marks → stored → block quote → global quote. */
export function resolveCommentAnchorRange(
  editor: Editor,
  comment: ChapterComment
): { from: number; to: number } | null {
  if (!editor?.state?.doc) return null;
  const doc = editor.state.doc;
  const docSize = doc.content.size;
  if (docSize <= 1) return null;

  const original = normalizeAnchorText(comment.targetText || comment.anchor?.excerpt || "");

  const markRange = findChapterCommentAnnotationRange(doc, comment.id);

  let storedRange: { from: number; to: number } | null = null;
  const af = comment.anchor?.from;
  const at = comment.anchor?.to;
  if (af != null && at != null && af < at) {
    const from = Math.max(0, Math.min(Math.floor(af), docSize));
    const to = Math.max(from + 1, Math.min(Math.floor(at), docSize));
    if (isValidTextRange(doc, from, to)) storedRange = { from, to };
  }

  const blockScopedRange = original
    ? findTargetTextInBlock(editor, comment.blockId, original)
    : null;
  const globalSearchRange = original ? findTargetTextRangeInEditor(editor, original) : null;

  return pickCommentAnchorRange({
    originalQuote: original,
    markRange:
      markRange && markRange.from < markRange.to && isValidTextRange(doc, markRange.from, markRange.to)
        ? markRange
        : null,
    storedRange,
    blockScopedRange,
    globalSearchRange,
    textAt: (range) => safeDocTextBetween(doc, range.from, range.to),
  });
}

function computeCommentIsStale(editor: Editor, comment: ChapterComment): boolean {
  const original = normalizeAnchorText(comment.targetText || "");
  if (!original) return false;
  return !docIncludesOriginal(editor, original);
}

/**
 * Reconcile stored anchors with the live editor (after hydrate, pull, or mark drift).
 * Preserves `targetText` (original highlight); updates `anchor` positions + current excerpt.
 */
export function reconcileChapterCommentsWithEditor(
  editor: Editor,
  comments: Chapter["comments"]
): Chapter["comments"] {
  let changed = false;
  const next = comments.map((c) => {
    if (c.resolved) return c;
    if (c.parentCommentId) return c; // replies are not anchored

    const resolved = resolveCommentAnchorRange(editor, c);
    const isStale = resolved == null ? computeCommentIsStale(editor, c) : false;

    if (!resolved) {
      if (c.isStale === isStale) return c;
      changed = true;
      return { ...c, isStale };
    }

    const excerpt = normalizeAnchorText(
      safeDocTextBetween(editor.state.doc, resolved.from, resolved.to)
    ).slice(0, 200);

    const anchorChanged =
      c.anchor?.from !== resolved.from ||
      c.anchor?.to !== resolved.to ||
      normalizeAnchorText(c.anchor?.excerpt || "") !== excerpt;
    const staleChanged = c.isStale !== isStale;

    if (!anchorChanged && !staleChanged) return c;
    changed = true;

    return {
      ...c,
      anchor: {
        from: resolved.from,
        to: resolved.to,
        excerpt: excerpt || c.anchor?.excerpt,
      },
      isStale,
    };
  });
  return changed ? next : comments;
}

/**
 * While typing: follow live ProseMirror marks (they track edits). Never overwrite `targetText`.
 */
export function syncChapterCommentsAnchorsFromEditor(
  editor: Editor,
  comments: Chapter["comments"]
): Chapter["comments"] {
  let changed = false;
  const next = comments.map((c) => {
    if (c.resolved) return c;
    if (c.parentCommentId) return c;

    const markRange = findChapterCommentAnnotationRange(editor.state.doc, c.id);
    const resolved =
      markRange && markRange.from < markRange.to ? markRange : resolveCommentAnchorRange(editor, c);
    const isStale = resolved == null ? computeCommentIsStale(editor, c) : false;

    if (!resolved) {
      if (c.isStale === isStale) return c;
      changed = true;
      return { ...c, isStale };
    }

    const excerpt = normalizeAnchorText(
      safeDocTextBetween(editor.state.doc, resolved.from, resolved.to)
    ).slice(0, 200);

    const prevFrom = c.anchor?.from;
    const prevTo = c.anchor?.to;
    const prevExcerpt = normalizeAnchorText(c.anchor?.excerpt || "");
    if (
      prevFrom === resolved.from &&
      prevTo === resolved.to &&
      prevExcerpt === excerpt &&
      c.isStale === isStale
    ) {
      return c;
    }

    changed = true;
    return {
      ...c,
      anchor: {
        from: resolved.from,
        to: resolved.to,
        excerpt: excerpt || c.anchor?.excerpt,
      },
      isStale,
    };
  });
  return changed ? next : comments;
}
